import React from 'react';
import { Link } from 'react-router-dom';

const copy = {
  en: {
    defaultTitle: 'Plan activity',
    actionRequired: 'Action required. Check your messages for next steps.',
    noActionRequired: 'No action is needed at this time.',
    viewMessages: 'View messages',
    moreDetails: 'View details',
    type: 'Type',
    caseManager: 'Case manager',
    trackingId: 'Tracking ID',
    startDate: 'Start date',
    endDate: 'End date',
    updatedAt: 'Last updated',
    status: 'Status',
    active: 'Active',
    upcoming: 'Upcoming',
    description: 'Description',
    contact: 'Send a message',
    dateOn: 'on',
    dateStarting: 'starting'
  },
  fr: {
    defaultTitle: 'Activité du plan',
    actionRequired: 'Action requise. Consultez vos messages pour connaître les prochaines étapes.',
    noActionRequired: 'Aucune action n’est requise pour le moment.',
    viewMessages: 'Voir les messages',
    moreDetails: 'Voir les détails',
    type: 'Type',
    caseManager: 'Gestionnaire de cas',
    trackingId: 'ID de suivi',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    updatedAt: 'Dernière mise à jour',
    status: 'Statut',
    active: 'Active',
    upcoming: 'À venir',
    description: 'Description',
    contact: 'Envoyer un message',
    dateOn: 'le',
    dateStarting: 'à partir du'
  }
};

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(locale);
};

const formatDateTime = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale);
};

const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDateKey = value => {
  if (!value) return '';
  if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatShortDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  if (language === 'fr') {
    return new Intl.DateTimeFormat('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = EN_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const stripLeadingCode = value => {
  if (!value) return '';
  const trimmed = String(value).trim();
  const withoutPrefix = trimmed.replace(/^\s*[A-Za-z0-9]+\s*[–—-]\s*/, '');
  if (/^\d+$/.test(withoutPrefix)) return '';
  return withoutPrefix;
};

function InterventionCard({ intervention, language }) {
  const t = copy[language] || copy.en;
  const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
  const rawTypeLabel = intervention?.type_label || intervention?.type_code || '';
  const cleanTypeLabel = stripLeadingCode(rawTypeLabel);
  const baseTitle = stripLeadingCode(intervention?.title || cleanTypeLabel || t.defaultTitle) || t.defaultTitle;
  const startKey = toDateKey(intervention?.start_date);
  const endKey = toDateKey(intervention?.end_date);
  const startLabel = formatShortDate(intervention?.start_date, language);
  const title = startLabel
    ? `${baseTitle} ${startKey && endKey && endKey !== startKey ? t.dateStarting : t.dateOn} ${startLabel}`
    : baseTitle;
  const typeLabel = cleanTypeLabel || '';
  const description = typeof intervention?.description === 'string'
    ? intervention.description.trim()
    : '';
  const actionRequired = Boolean(intervention?.action_required);
  const stateLabel = intervention?.state === 'upcoming'
    ? t.upcoming
    : (intervention?.state === 'active' ? t.active : (intervention?.state || ''));

  const detailsRows = [
    typeLabel && typeLabel !== baseTitle ? { label: t.type, value: typeLabel } : null,
    intervention?.case_manager_name ? { label: t.caseManager, value: intervention.case_manager_name } : null,
    intervention?.tracking_id ? { label: t.trackingId, value: intervention.tracking_id } : null,
    intervention?.start_date ? { label: t.startDate, value: formatDate(intervention.start_date, locale) } : null,
    intervention?.end_date ? { label: t.endDate, value: formatDate(intervention.end_date, locale) } : null,
    intervention?.updated_at ? { label: t.updatedAt, value: formatDateTime(intervention.updated_at, locale) } : null,
    stateLabel ? { label: t.status, value: stateLabel } : null,
    description ? { label: t.description, value: description } : null
  ].filter(Boolean);

  return (
    <div
      className={`govuk-notification-banner app-dashboard-card${actionRequired ? ' govuk-notification-banner--urgent' : ''}`}
      role="region"
      aria-labelledby={`intervention-title-${intervention?.intervention_id || intervention?.tracking_id || 'card'}`}
      data-module="govuk-notification-banner"
    >
      <div className="govuk-notification-banner__header">
        <h2 className="govuk-notification-banner__title" id={`intervention-title-${intervention?.intervention_id || intervention?.tracking_id || 'card'}`}>
          {title}
        </h2>
      </div>
      <div className="govuk-notification-banner__content">
        {typeLabel && typeLabel !== title && (
          <p className="govuk-body govuk-!-margin-bottom-2">{typeLabel}</p>
        )}
        <p className="govuk-body govuk-!-font-weight-bold govuk-!-margin-bottom-3">
          {actionRequired ? t.actionRequired : t.noActionRequired}
          {actionRequired && (
            <>
              {' '}
              <Link to="/messages" className="govuk-link">{t.viewMessages}</Link>
            </>
          )}
        </p>
        {detailsRows.length > 0 && (
          <details className="govuk-details govuk-!-margin-bottom-3">
            <summary className="govuk-details__summary">
              <span className="govuk-details__summary-text">{t.moreDetails}</span>
            </summary>
            <div className="govuk-details__text">
              <dl className="govuk-summary-list">
                {detailsRows.map((row) => (
                  <div className="govuk-summary-list__row" key={row.label}>
                    <dt className="govuk-summary-list__key">{row.label}</dt>
                    <dd className="govuk-summary-list__value">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </details>
        )}
        <div className="govuk-button-group">
          <Link to="/messages/new" className="govuk-button govuk-button--secondary">
            {t.contact}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default InterventionCard;
